import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAttendanceFlags } from '@/hooks/attendance';
import { useCompanyProfiles } from '@/hooks/timesheets/useCompanyProfiles';
import { useHasPermission } from '@/hooks/permissions';
import { PERMISSION_KEYS } from '@/types/roles';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Filter, AlertCircle, Info, CheckCircle, RefreshCw, Download, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { exportAttendanceFlagsToCSV } from '@/utils/csvExport';
import type { AttendanceFlag, AttendanceFlagFilters } from '@/services/attendance/types';
import { VirtualList } from '@/components/ui/virtual-list';

const PAGE_SIZE = 50;


export function AttendanceActivityLogs() {
  const { currentCompany } = useAuth();
  const canApproveTimesheets = useHasPermission(PERMISSION_KEYS.APPROVE_TIMESHEETS);
  const [showFilters, setShowFilters] = useState(false);
  const { data: companyProfiles } = useCompanyProfiles();
  const [page, setPage] = useState(0);
  const [accumulatedFlags, setAccumulatedFlags] = useState<AttendanceFlag[]>([]);
  
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
    profileId: undefined as string | undefined,
    flagType: 'all',
    flagSeverity: 'all',
    resolved: undefined as boolean | undefined,
  });

  const appliedFilters: AttendanceFlagFilters = {
    ...filters,
    flagType: filters.flagType === 'all' ? '' : filters.flagType,
    flagSeverity: filters.flagSeverity === 'all' ? '' : filters.flagSeverity,
  };

  const { data: flags, totalCount, hasMore, isLoading, resolveFlag, isResolving, triggerDetection, isDetecting } = useAttendanceFlags(
    currentCompany?.id,
    appliedFilters,
    { limit: PAGE_SIZE, offset: page * PAGE_SIZE }
  );

  // Reset pagination when filters change
  useEffect(() => {
    setPage(0);
    setAccumulatedFlags([]);
  }, [filters.startDate?.getTime(), filters.endDate?.getTime(), filters.profileId, filters.flagType, filters.flagSeverity, filters.resolved]);

  // Accumulate results for Load More
  useEffect(() => {
    if (flags) {
      if (page === 0) {
        setAccumulatedFlags(flags);
      } else {
        setAccumulatedFlags(prev => {
          const existingIds = new Set(prev.map(f => f.id));
          const newItems = flags.filter(f => !existingIds.has(f.id));
          return [...prev, ...newItems];
        });
      }
    }
  }, [flags, page]);

  // Derive sorted profiles from company directory (not from loaded flags)
  const profiles = (companyProfiles || [])
    .map(p => ({ id: p.id, first_name: p.first_name, last_name: p.last_name }))
    .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || ''));

  const [resolveDialog, setResolveDialog] = useState<{ open: boolean; flag: AttendanceFlag | null }>({
    open: false,
    flag: null,
  });
  const [resolutionNote, setResolutionNote] = useState('');

  const handleResolve = () => {
    if (resolveDialog.flag) {
      resolveFlag(
        { flagId: resolveDialog.flag.id, resolutionNote },
        {
          onSuccess: () => {
            setResolveDialog({ open: false, flag: null });
            setResolutionNote('');
          },
        }
      );
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-warning" />;
      case 'info':
        return <Info className="h-4 w-4 text-info" />;
      default:
        return null;
    }
  };

  const getFlagTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      EARLY_IN: 'Early Check-in',
      LATE_IN: 'Late Check-in',
      EARLY_OUT: 'Early Check-out',
      LATE_OUT: 'Late Check-out',
      UNSCHEDULED: 'Unscheduled',
      MISSED_IN: 'Missed Check-in',
      MISSED_OUT: 'Missed Check-out',
      OUTSIDE_GEOFENCE: 'Outside Geofence',
    };
    return labels[type] || type;
  };

  const handleExportCSV = () => {
    if (accumulatedFlags.length > 0) {
      exportAttendanceFlagsToCSV(accumulatedFlags);
    }
  };

  const renderFlagCard = (flag: AttendanceFlag, index: number) => (
    <Card key={flag.id} className={flag.auto_resolved ? 'opacity-60' : ''}>
      <CardContent className="py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            {getSeverityIcon(flag.flag_severity)}
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={flag.flag_severity === 'error' ? 'destructive' : 'secondary'}>
                  {getFlagTypeLabel(flag.flag_type)}
                </Badge>
                <span className="text-sm font-medium">
                  {flag.profile?.first_name} {flag.profile?.last_name}
                </span>
                <span className="text-sm text-muted-foreground">
                  {format(new Date(flag.event_date), 'MMM d, yyyy')}
                </span>
                {(flag.event_time || flag.scheduled_time) && (
                  <span className="text-sm text-muted-foreground">
                    {flag.event_time 
                      ? `at ${flag.event_time.substring(0, 5)}`
                      : `scheduled ${flag.scheduled_time?.substring(0, 5)}`
                    }
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{flag.flag_reason}</p>
              {flag.deviation_minutes !== null && (
                <p className="text-xs text-muted-foreground">
                  Deviation: {Math.abs(flag.deviation_minutes)} minutes
                </p>
              )}
              {flag.auto_resolved && flag.resolution_note && (
                <p className="text-xs text-muted-foreground italic">
                  Resolved: {flag.resolution_note}
                </p>
              )}
            </div>
          </div>
          {!flag.auto_resolved && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setResolveDialog({ open: true, flag })}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Resolve
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (isLoading && page === 0) {
    return (
      <Card>
        <CardContent className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Attendance Flags</CardTitle>
              <CardDescription>
                Review and manage attendance discrepancies
                {totalCount > 0 && (
                  <span className="ml-1">({totalCount} total)</span>
                )}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleExportCSV}
                disabled={accumulatedFlags.length === 0}
                variant="outline"
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Export ({accumulatedFlags.length} loaded)
              </Button>
              {canApproveTimesheets && (
                <Button
                  onClick={() => triggerDetection()}
                  disabled={isDetecting}
                  variant="outline"
                  size="sm"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isDetecting ? 'animate-spin' : ''}`} />
                  Re-run Detection
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Filters Section */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 p-4 border rounded-lg bg-muted/50">
              <div className="space-y-2">
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value ? new Date(e.target.value) : undefined })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value ? new Date(e.target.value) : undefined })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="employee">Employee</Label>
                <Select
                  value={filters.profileId || 'all'}
                  onValueChange={(value) => 
                    setFilters({ ...filters, profileId: value === 'all' ? undefined : value })
                  }
                >
                  <SelectTrigger id="employee">
                    <SelectValue placeholder="All employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All employees</SelectItem>
                    {profiles.map((profile) => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {`${profile.first_name || ''} ${profile.last_name || ''}`.trim()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="flag-type">Flag Type</Label>
                <Select 
                  value={filters.flagType} 
                  onValueChange={(value) => setFilters({ ...filters, flagType: value })}
                >
                  <SelectTrigger id="flag-type">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="EARLY_IN">Early Check-in</SelectItem>
                    <SelectItem value="LATE_IN">Late Check-in</SelectItem>
                    <SelectItem value="EARLY_OUT">Early Check-out</SelectItem>
                    <SelectItem value="LATE_OUT">Late Check-out</SelectItem>
                    <SelectItem value="UNSCHEDULED">Unscheduled</SelectItem>
                    <SelectItem value="MISSED_IN">Missed Check-in</SelectItem>
                    <SelectItem value="MISSED_OUT">Missed Check-out</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="severity">Severity</Label>
                <Select
                  value={filters.flagSeverity}
                  onValueChange={(value) => setFilters({ ...filters, flagSeverity: value })}
                >
                  <SelectTrigger id="severity">
                    <SelectValue placeholder="All severities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All severities</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={filters.resolved === undefined ? 'all' : filters.resolved.toString()}
                  onValueChange={(value) =>
                    setFilters({
                      ...filters,
                      resolved: value === 'all' ? undefined : value === 'true',
                    })
                  }
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="false">Open</SelectItem>
                    <SelectItem value="true">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Flags List */}
          <div>
            {accumulatedFlags.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No attendance flags found</p>
                <p className="text-sm mt-2">Flags will appear here when attendance discrepancies are detected</p>
              </div>
            ) : (
              <VirtualList
                items={accumulatedFlags}
                height={600}
                itemHeight={140}
                renderItem={renderFlagCard}
                className="pr-4"
                overscan={3}
              />
            )}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => setPage(prev => prev + 1)}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Load More ({accumulatedFlags.length} of {totalCount})
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialog.open} onOpenChange={(open) => setResolveDialog({ open, flag: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Attendance Flag</DialogTitle>
            <DialogDescription>
              Mark this flag as resolved and optionally add a note explaining the resolution.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="resolution-note">Resolution Note (Optional)</Label>
              <Textarea
                id="resolution-note"
                placeholder="Explain why this flag is being resolved..."
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog({ open: false, flag: null })}>
              Cancel
            </Button>
            <Button onClick={handleResolve} disabled={isResolving}>
              {isResolving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Resolve Flag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
